import { useState, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { createTender, uploadTenderFile } from '../api/tenders';
import type { TenderCreatePayload } from '../types';

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function parseList(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// -------------------------------------------------------------------------
// Form state
// -------------------------------------------------------------------------

interface FormState {
  title: string;
  client: string;
  description: string;
  requirements: string;      // textarea — split on save
  required_skills: string;   // textarea — split on save
  nice_to_have_skills: string;
  deadline: string;
  work_location: string;
  sector: string;
  budget_min: string;
  budget_max: string;
  currency: string;
}

const INITIAL_FORM: FormState = {
  title: '',
  client: '',
  description: '',
  requirements: '',
  required_skills: '',
  nice_to_have_skills: '',
  deadline: '',
  work_location: '',
  sector: '',
  budget_min: '',
  budget_max: '',
  currency: 'EUR',
};

// -------------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------------

const ALLOWED_UPLOAD_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'message/rfc822',
];
const ALLOWED_UPLOAD_EXT = ['.pdf', '.doc', '.docx', '.eml'];
const MAX_UPLOAD_SIZE_MB = 20;

export default function TenderSubmit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // File upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change
    if (errors[name as keyof FormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.title.trim()) newErrors.title = t('tenderSubmit.validation.titleRequired');
    if (!form.client.trim()) newErrors.client = t('tenderSubmit.validation.clientRequired');
    if (!form.description.trim()) newErrors.description = t('tenderSubmit.validation.descriptionRequired');
    if (!form.deadline) newErrors.deadline = t('tenderSubmit.validation.deadlineRequired');
    else if (new Date(form.deadline) <= new Date()) {
      newErrors.deadline = t('tenderSubmit.validation.deadlineFuture');
    }
    if (!form.required_skills.trim()) {
      newErrors.required_skills = t('tenderSubmit.validation.requiredSkillsRequired');
    }

    const budgetMin = form.budget_min ? parseFloat(form.budget_min) : undefined;
    const budgetMax = form.budget_max ? parseFloat(form.budget_max) : undefined;
    if (budgetMin !== undefined && isNaN(budgetMin)) {
      newErrors.budget_min = t('tenderSubmit.validation.validNumber');
    }
    if (budgetMax !== undefined && isNaN(budgetMax)) {
      newErrors.budget_max = t('tenderSubmit.validation.validNumber');
    }
    if (budgetMin !== undefined && budgetMax !== undefined && budgetMin > budgetMax) {
      newErrors.budget_min = t('tenderSubmit.validation.minExceedsMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError(null);

    const payload: TenderCreatePayload = {
      title: form.title.trim(),
      client: form.client.trim(),
      description: form.description.trim(),
      requirements: parseList(form.requirements),
      required_skills: parseList(form.required_skills),
      nice_to_have_skills: parseList(form.nice_to_have_skills),
      deadline: form.deadline,
      work_location: form.work_location.trim() || undefined,
      sector: form.sector.trim() || undefined,
      budget_min: form.budget_min ? parseFloat(form.budget_min) : undefined,
      budget_max: form.budget_max ? parseFloat(form.budget_max) : undefined,
      currency: form.currency || undefined,
    };

    try {
      const created = await createTender(payload);
      navigate(`/tenders/${created.id}/matches`, { state: { justCreated: true } });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t('tenderSubmit.submissionFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setForm(INITIAL_FORM);
    setErrors({});
    setServerError(null);
  }

  function handleUploadFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setUploadFile(null);
    setUploadError(null);
    if (!file) return;

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_UPLOAD_MIME.includes(file.type) && !ALLOWED_UPLOAD_EXT.includes(ext)) {
      setUploadError(t('tenderSubmit.onlyPdfWord'));
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      setUploadError(t('tenderSubmit.fileTooLarge', { maxSize: MAX_UPLOAD_SIZE_MB }));
      return;
    }
    setUploadFile(file);
  }

  async function handleFileUpload(e: FormEvent) {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const created = await uploadTenderFile(
        uploadFile,
        uploadTitle.trim() || undefined,
        setUploadProgress
      );
      setUploadSuccess(t('tenderSubmit.tenderCreated', { title: created.title }));
      setUploadFile(null);
      setUploadTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Navigate to matches after short delay so user sees success message
      setTimeout(() => navigate(`/tenders/${created.id}/matches`, { state: { justCreated: true } }), 1500);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t('tenderSubmit.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }

  // Helper for consistent field rendering
  function field(
    id: keyof FormState,
    label: string,
    type: 'input' | 'textarea' | 'select' | 'date' | 'number',
    options?: { placeholder?: string; required?: boolean; hint?: string; options?: string[] }
  ) {
    const hasError = Boolean(errors[id]);
    const ariaDesc = `${id}-hint`;
    const errorId = `${id}-error`;

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="form-label">
          {label}
          {options?.required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
        </label>

        {options?.hint && (
          <p id={ariaDesc} className="text-xs text-gray-400">{options.hint}</p>
        )}

        {type === 'textarea' ? (
          <textarea
            id={id}
            name={id}
            value={form[id]}
            onChange={handleChange}
            rows={3}
            placeholder={options?.placeholder}
            required={options?.required}
            aria-required={options?.required}
            aria-describedby={[options?.hint ? ariaDesc : '', hasError ? errorId : ''].filter(Boolean).join(' ') || undefined}
            aria-invalid={hasError}
            className={['form-input resize-none', hasError ? 'border-red-400 focus:ring-red-400' : ''].join(' ')}
          />
        ) : type === 'select' ? (
          <Select
            inputId={id}
            options={(options?.options ?? []).map((opt) => ({ value: opt, label: opt }))}
            value={form[id] ? { value: form[id], label: form[id] } : null}
            onChange={(opt) => handleChange({ target: { name: id, value: opt?.value ?? '' } } as React.ChangeEvent<HTMLInputElement>)}
            classNamePrefix="react-select"
            isSearchable={false}
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px', ...(hasError ? { borderColor: '#f87171' } : {}) }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        ) : (
          <input
            id={id}
            name={id}
            type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
            value={form[id]}
            onChange={handleChange}
            placeholder={options?.placeholder}
            required={options?.required}
            aria-required={options?.required}
            min={type === 'date' ? new Date().toISOString().split('T')[0] : undefined}
            aria-describedby={[options?.hint ? ariaDesc : '', hasError ? errorId : ''].filter(Boolean).join(' ') || undefined}
            aria-invalid={hasError}
            className={['form-input', hasError ? 'border-red-400 focus:ring-red-400' : ''].join(' ')}
          />
        )}

        {hasError && (
          <p id={errorId} role="alert" className="text-xs text-red-600">
            {errors[id]}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('tenderSubmit.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('tenderSubmit.subtitle', {
            defaultValue: 'Fill in the tender details. Fields marked * are required.',
          })}
        </p>
      </header>

      {/* File upload section */}
      <section aria-label={t('tenderSubmit.uploadSectionAria')} className="card space-y-4">
        <h2 className="text-base font-semibold text-gray-900">{t('tenderSubmit.uploadSection')}</h2>
        <p className="text-xs text-gray-500">
          {t('tenderSubmit.uploadHint')}
        </p>

        {uploadSuccess && (
          <div role="status" aria-live="polite" className="rounded bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
            {uploadSuccess}
          </div>
        )}

        <form onSubmit={handleFileUpload} noValidate aria-label={t('tenderSubmit.uploadFormAria')} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="tender-title-upload" className="form-label">
                {t('tenderSubmit.titleLabel')} <span className="text-xs text-gray-400">{t('tenderSubmit.titleOptional')}</span>
              </label>
              <input
                id="tender-title-upload"
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder={t('tenderSubmit.titlePlaceholder')}
                className="form-input"
              />
            </div>

            <div>
              <label htmlFor="tender-file" className="form-label">
                {t('tenderSubmit.documentLabel', { maxSize: MAX_UPLOAD_SIZE_MB })}{' '}
                <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="tender-file"
                type="file"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx,.eml"
                required
                aria-required="true"
                onChange={handleUploadFileChange}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border file:border-gray-300 file:text-xs file:bg-white file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              />
            </div>
          </div>

          {uploadError && (
            <p role="alert" className="text-sm text-red-600">{uploadError}</p>
          )}

          {uploadProgress !== null && (
            <div aria-label={t('tenderSubmit.uploadProgress', { progress: uploadProgress })} className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-600 transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <p className="text-xs text-gray-500">{t('tenderSubmit.percentUploaded', { progress: uploadProgress })}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!uploadFile || uploading}
            className="btn-primary"
            aria-busy={uploading}
          >
            {uploading ? t('tenderSubmit.uploading') : t('tenderSubmit.uploadAndCreate')}
          </button>
        </form>
      </section>

      <div className="relative flex items-center justify-center">
        <span className="bg-white px-3 text-sm text-gray-400">{t('tenderSubmit.orManually')}</span>
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
      </div>

      {/* Server error */}
      {serverError && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {serverError}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        onReset={handleReset}
        noValidate
        aria-label={t('tenderSubmit.formAria')}
        className="card space-y-5"
      >
        {/* Basic info */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2 w-full">
            {t('tenderSubmit.tenderInfo')}
          </legend>

          {field('title', t('tenderSubmit.tenderTitle'), 'input', {
            required: true,
            placeholder: t('tenderSubmit.tenderTitlePlaceholder'),
          })}

          {field('client', t('tenderSubmit.clientLabel'), 'input', {
            required: true,
            placeholder: t('tenderSubmit.clientPlaceholder'),
          })}

          {field('description', t('tenderSubmit.descriptionLabel'), 'textarea', {
            required: true,
            placeholder: t('tenderSubmit.descriptionPlaceholder'),
          })}

          {field('requirements', t('tenderSubmit.requirementsLabel'), 'textarea', {
            hint: t('tenderSubmit.requirementsHint'),
            placeholder: t('tenderSubmit.requirementsPlaceholder'),
          })}

          <div className="grid sm:grid-cols-2 gap-4">
            {field('work_location', t('tenderSubmit.workLocationLabel'), 'input', {
              placeholder: t('tenderSubmit.workLocationPlaceholder'),
              hint: t('tenderSubmit.workLocationHint'),
            })}

            {field('sector', t('tenderSubmit.sectorLabel'), 'input', {
              placeholder: t('tenderSubmit.sectorPlaceholder'),
              hint: t('tenderSubmit.sectorHint'),
            })}
          </div>
        </fieldset>

        {/* Skills */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2 w-full">
            {t('tenderSubmit.skills')}
          </legend>

          {field('required_skills', t('tenderSubmit.requiredSkills'), 'textarea', {
            required: true,
            hint: t('tenderSubmit.requiredSkillsHint'),
            placeholder: t('tenderSubmit.requiredSkillsPlaceholder'),
          })}

          {field('nice_to_have_skills', t('tenderSubmit.niceToHaveSkills'), 'textarea', {
            hint: t('tenderSubmit.niceToHaveSkillsHint'),
            placeholder: t('tenderSubmit.niceToHaveSkillsPlaceholder'),
          })}
        </fieldset>

        {/* Deadline & budget */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-2 w-full">
            {t('tenderSubmit.timelineBudget')}
          </legend>

          {field('deadline', t('tenderSubmit.submissionDeadline'), 'date', { required: true })}

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              {field('budget_min', t('tenderSubmit.budgetMin'), 'number', { placeholder: '80000' })}
            </div>
            <div className="col-span-1">
              {field('budget_max', t('tenderSubmit.budgetMax'), 'number', { placeholder: '120000' })}
            </div>
            <div className="col-span-1">
              {field('currency', t('tenderSubmit.currency'), 'select', {
                options: ['EUR', 'USD', 'GBP', 'CHF'],
              })}
            </div>
          </div>
        </fieldset>

        {/* Form actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
            aria-busy={submitting}
          >
            {submitting ? t('tenderSubmit.submitting') : t('tenderSubmit.submitTender')}
          </button>
          <button type="reset" className="btn-secondary">
            {t('common.reset')}
          </button>
        </div>
      </form>
    </div>
  );
}
